/**
 * auth.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { ActivatedRoute, Route, Router, UrlSegment } from "@angular/router";
import { Modal } from "angular2-modal/plugins/bootstrap";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs/Rx";

import { Alert, AlertService } from "../common/alert.service";
import { MessageReceiver } from "../server/message-receiver";
import { SecurePrompt } // tslint:disable-next-line
  from "../server/messages/auth/credentials/modify-security-questions-client-response";
import {
  AuthTokenLoginRequest,
  AuthTokenRequest,
  AuthTokenResponse,
  DisconnectDownstreamMessage,
  DisconnectUpstreamMessage,
  LoginRequest,
  StartGuestSessionRequest
} from "../server/messages/auth/";
import {
  BeginPasswordResetRequest,
  ChangePasswordRequest,
  CredentialChallengeClientResponse,
  CredentialChallengeServerRequest,
  ModifySecurityQuestionsClientResponse,
  ModifySecurityQuestionsServerRequest,
  NewPasswordClientResponse,
  NewPasswordServerRequest,
  SendPasswordResetEmailRequest
} from "../server/messages/auth/credentials/";
import {
  AcknowledgmentResponse,
  DISCONNECTION_TEXT,
  Message,
  MessageTag,
  ResponseCode,
  ReplyMessage
} from "../server/messages/general";
import { MessagingService } from "../server/messaging.service";
import { disconnectValue } from "../support/types/decorators";

import { Hasher } from "./hasher.service";
import { PasswordCredentials } from "./identity/credentials";
import { Salter } from "./salter.service";
import { ResetUserPasswordRequest } from "app/server/messages/group-management/reset-user-password-request";
import { ConfigurationService } from "app/configuration/configuration.service";

/**
 * AuthState represents the authentication status of the current connection.
 */
export enum AuthState {
  Unauthenticated,
  PasswordReset,
  EnforcedCredentialChange,
  Authenticated = 100,
}

interface SessionDependencyDeclaring {
  existingSessionOnly : boolean;
}

interface Preauth {
  token : string;
  target : string;
  message? : Alert;
}

const LOCAL_TOKEN_LIFETIME : number = 10 * 1000;

const DEFAULT_POST_AUTH_TARGET : string = "/home";

const DEFAULT_UNAUTHED_TARGET : string = "/";

/**
 * The {@link AuthService} handles authentication and authorization requests and
 * data for the user, and provides helper methods for those requests and
 * transitions.
 */
@Injectable()
export class AuthService implements MessageReceiver
{
  /**
   * Internal authentication state flag.
   * @type {AuthState}
   */
  @disconnectValue(AuthState.Unauthenticated)
  public state : AuthState = AuthState.Unauthenticated;

  // TODO: this needs to be more ephemeral. As it stands, once it's set, no
  // matter how much navigation we do, it will persist until cleared. Perhaps
  // it can instead be a route parameter?
  /**
   * The route to redirect to on the next auth redirect (typically used after a
   * successful login).
   */
  @disconnectValue(null)
  public redirectTo : string[] | null;

  @disconnectValue(null)
  public specialRedirectBehavior : () => void | null;

  /**
   * Create the {@link AuthService}.
   */
  public constructor (
    protected messaging : MessagingService,
    protected router : Router,
    protected activatedRoute : ActivatedRoute,
    public hasher : Hasher,
    public salter : Salter,
    protected alerts : AlertService,
    public modal : Modal,
    public config : ConfigurationService
  )
  {
    this.messaging.ready.subscribe(
      (on : boolean) => on ? this.connected() : this.disconnected()
      // (error : any) => this.finished(error),
      // () => this.finished({completed: true})
    );
    if (config.client.type === "guest") {
      // nvm 
    }
  }

  /**
   * Flag indicating whether we have connected to the server before.
   */
  private firstConnection : boolean = true;

  /**
   * Initialization for when the {@link MessagingService} is ready.
   */
  public connected () : void
  {
    if (
        window.opener
        && window.opener !== null
        && typeof window.opener.getPreauth === "function"
    ) {
      const request : Preauth | null = window.opener.getPreauth();
      if (request)
      {
        this.preauthenticate(request);
      }
    }

    this.messaging.receive(MessageTag.RSP_ACKNOWLEDGEMENT).subscribe(
      (m : AcknowledgmentResponse) => {
        this.inferStatusFromAck(m);
        this.firstConnection = false;
    });

    this.messaging.receive(MessageTag.DOWN_DISCONNECT).subscribe(
      (m : DisconnectDownstreamMessage) => this.handleServerDrop(m)
    );
  }

  /**
   * Teardown instructions for when the {@link MessagingService} has stopped.
   */
  public disconnected () : void {/* decorator-generated */}

  /**
   * Record a successful authentication attempt
   */
  private authSucceeded () : boolean
  {
    this.state = AuthState.Authenticated;
    return true;
  }
  /**
   * Record an unsuccessful authentication attempt
   */
  private authFailed () : boolean
  {
    this.state = AuthState.Unauthenticated;
    return false;
  }

  /**
   * Use an {@link AcknowledgmentInfoMessage} to determine the connection's
   * current authentication state. This is primarily used to read the response
   * to an auth attempt (with the second parameter set to `true`), however it
   * also provides a sanity check for when other info messages contain auth
   * details in their status codes (ex. AlreadyAuthenticated and LoginFailure).
   */
  public inferStatusFromAck (
    message : AcknowledgmentResponse,
    wasAuthRequest? : boolean
  ) : boolean
  {
    if (message.code === ResponseCode.LoginFailure)
    {
      return this.authFailed();
    }

    // NOTE: in the future we'll probably have to pair actual request messages
    // with their responses so we can detect other kinds of requests
    if (wasAuthRequest)
    {
      if (message.ok || message.code === ResponseCode.OK)
      {
        // If the message was an auth request, we treat an ok as a success
        return this.authSucceeded();
      }
      else
      {
        // We treat all other responses as failures and assume still not authed
        return this.authFailed();
      }
    }
    else
    {
      // The message didn't indicate anything specific about the authentication
      // status. We still return false, but we do not call one of the state
      // transition/assertion functions.
      return false;
    }
  }

  /**
   * A flag indicating whether the connection is in the AUTHENTICATED state.
   */
  get authenticated () : boolean
  {
    return this.state === AuthState.Authenticated;
  }
  /**
   * A flag indicating whether the connection is in the PASSWORD RESET state.
   */
  get resettingPassword () : boolean
  {
    return this.state === AuthState.PasswordReset;
  }

  public boot () : void
  {
    this.router.navigate([DEFAULT_UNAUTHED_TARGET]);
  }

  /**
   * Redirect to a previously stored route, or home, if no route had been stored
   */
  public redirect () : void
  {
    if (this.specialRedirectBehavior) {
      const fn : () => void = this.specialRedirectBehavior;
      this.specialRedirectBehavior = null;
      return fn();
    }
    const target : string[] = this.redirectTo || [DEFAULT_POST_AUTH_TARGET];
    this.redirectTo = null;
    this.router.navigate(target);
  }

  /**
   * Redirect to a previously stored route if the connection is authenticated
   */
  public redirectIfAuthed () : void
  {
    if (this.authenticated)
    {
      this.redirect();
    }
  }

  public openPreauthedTab (target? : string) : void
  {
    const authedWindow : Window = window.open("/");
    // TODO: perhaps we should have a static preauth landing page (with no auth
    // requirements) so initialization is more limited. The page could also have
    // a message like "if you aren't redirected in a few seconds, the server may
    // have encountered an error with your authentication" or something...

    let message : Alert = null;

    if (!target)
    {
      // default target is simply the current URL
      if (this.routeIsSessionDependent(this.activatedRoute))
      {
        target = DEFAULT_POST_AUTH_TARGET;
        message = {
          message:
            `You have been authenticated! However, you cannot open a subject
            context into a new tab for security reasons.`,
          type: "warning",
          timeout: this.alerts.getDefaultTimeout()
        };
      }
      else
      {
        target = this.router.routerState.snapshot.url;
      }
      console.log(`Assuming target of ${target}`);
    }

    this.messaging.converse(new AuthTokenRequest())
      .map((msg : AuthTokenResponse) => msg.token)
      .catch((err : any) => Observable.of(""))
      .subscribe((token : string) => {
        // (<any> authedWindow).PREAUTH_TOKEN = token;
        this.createSingleUseTokenGetter({token, target, message});
      });
  }

  private createSingleUseTokenGetter (request : Preauth) : void
  {
    (window as any).getPreauth = () => {
      (window as any).getPreauth = null;
      return request;
    };
    setTimeout(() : void => request = null, LOCAL_TOKEN_LIFETIME);
  }

  private preauthenticate (request : Preauth) : void
  {
    this.messaging.successful(
      new AuthTokenLoginRequest(request.token)
    ).subscribe(() => {
      this.authSucceeded();
      this.router.navigate([request.target]);
      if (request.message)
      {
        this.alerts.add(request.message);
      }
    });
  }

  /**
   * Render a standard disconnection message for a particular response code
   * to the {@link AlertService}
   */
  private alertDisconnect (c : ResponseCode, soft : boolean = false) : void
  {
    if (soft)
    {
      this.alerts.warnAndReplace(
        DISCONNECTION_TEXT.get(c) || DISCONNECTION_TEXT.default, "disconnect");
    }
    else
    {
      this.alerts.errorAndReplace(
        DISCONNECTION_TEXT.get(c) || DISCONNECTION_TEXT.default, "disconnect");
    }
  }

  /**
   * React to the server signal for disconnection.
   */
  // tslint:disable-next-line:cyclomatic-complexity
  private handleServerDrop (m : DisconnectDownstreamMessage) : void
  {
    let bailMessage : string;
    switch (m.code)
    {
      case ResponseCode.META_AlreadyHandled:
        // This code is an internal-only flag to indicate that another service
        // has already taken responsibility for the disconnect.
        return;
      case ResponseCode.OK:
        this.messaging.send(new DisconnectUpstreamMessage()).subscribe();
        this.router.navigate([DEFAULT_UNAUTHED_TARGET]);
        this.messaging.restart();
        return;
      case ResponseCode.LoginFailure:
      case ResponseCode.SystemDown:
      case ResponseCode.Maintenance:
        this.alertDisconnect(m.code);
        return;
      case ResponseCode.PermissionDenied:
        bailMessage = "Algorithm was stopped due to permissions failure.";
        // which cascades into...
      case ResponseCode.InvalidRequest:
      case ResponseCode.RequestExpired:
      case ResponseCode.RequestFailed:
      case ResponseCode.DuplicateEmail:
      case ResponseCode.DataCorruption:
      case ResponseCode.UnspecifiedError:
      default:
        this.alertDisconnect(m.code);
        break;
    }
    // generic restart and bail
    this.messaging.restart();
    this.bailIfNeeded(bailMessage);
  }

  private bailIfNeeded (msg? : string) : void
  {
    if (this.routeIsSessionDependent(this.activatedRoute))
    {
      // TODO: reexamine this in the light of alg/subj services
      this.alerts.add({
        message: `Because you had a subject open during a disconnect, you may
                  need to reload the page.`,
        type: "error",
        actionLabel: "Reload",
        action: () => window.location.reload(false)
      });
      // TODO: this is a core part of whenever we try to improve our "recovery"
      // features. Maybe a decent interim would be to force a browser refresh,
      // but we'd want a mechanism to pass a displayable message to the next
      // session (in case the user missed it here).
    }
  }

  private routeIsSessionDependent (route : ActivatedRoute) : boolean
  {
    if (!(route instanceof ActivatedRoute))
    {
      return false;
    }
    if ((route.snapshot.data as SessionDependencyDeclaring).existingSessionOnly)
    {
      return true;
    }
    if (route.firstChild)
    {
      // console.log(route.children);
      return this.routeIsSessionDependent(route.firstChild);
    }
    return false;
  }

  public changePassword (newPassword : string, oldPassword : string)
    : Observable<Message>
  {
    const msg : Message = new ChangePasswordRequest(
      this.hasher.hexOf(this.salter.salt(newPassword)),
      this.hasher.hexOf(this.salter.salt(oldPassword))
    );
    return this.messaging.send(msg);
  }

  public resetPassword (objectId: number, password: string): Observable<Message> {
    const msg: Message = new ResetUserPasswordRequest(objectId, this.hasher.hexOf(this.salter.salt(password)));
    return this.messaging.send(msg);
  }

  public changePrompts (
    prompts : Array<{question : string, answer : string}>
  ) : Observable<Message>
  {
    const pairs : SecurePrompt[] = prompts.map(
      ({question, answer} : {question : string, answer : string}) => {
        return {
          prompt: question,
          response: this.hasher.hexOf(this.salter.salt(answer))
        };
    });
    return this.messaging.send(
      new ModifySecurityQuestionsClientResponse(pairs));
  }

  public requestResetEmail (login : string) : Observable<boolean>
  {
    return this.messaging.resultOf(new SendPasswordResetEmailRequest(login));
  }

  /**
   * The prompt subject is returned by the initial beginResetFlow call; it
   * emits new prompts as they are received from the server, errors if any of
   * the requests fail, and completes if all of the answers are successful (and
   * the server is ready to receive a new password).
   */
  private prompt : Subject<string>;
  private promptSub : Subscription;

  private resetFailure (failedOnToken : boolean, err? : any) : void
  {
    console.error(err);
    if (failedOnToken)
    {
      this.prompt.error(
          new Error("Your reset token was invalid or expired."));
    }
    else
    {
      this.prompt.error(
          new Error("The server did not accept your answer."));
    }
    this.promptSub.unsubscribe();
  }

  public beginResetFlow (token : string) : Observable<string>
  {
    this.prompt = new BehaviorSubject<string>("Loading...");
    this.messaging.send(new BeginPasswordResetRequest(token)).subscribe(
      (reply : any) => {
        if (reply.code === ResponseCode.RequestExpired)
        {
          this.resetFailure(true);
        }
      },
      (err : any) => this.resetFailure(true, err)
    );
    this.promptSub = this.messaging.receive(
      MessageTag.SREQ_CREDENTIAL_CHALLENGE
    ).subscribe(
      (msg : CredentialChallengeServerRequest) => this.prompt.next(msg.prompt));
    this.promptSub.add(
      this.messaging.receive(MessageTag.SREQ_NEW_PASSWORD).subscribe(
        () => {
          this.prompt.complete();
          this.promptSub.unsubscribe();
        }));
    return this.prompt;
  }

  public supplySecureAnswer (answer : string) : void
  {
    this.messaging.send(new CredentialChallengeClientResponse(
      this.hasher.hexOf(this.salter.salt(answer)))).subscribe(
        {error : (err : any) => this.resetFailure(false, err)});
  }

  public finishResetFlow (newPassword : string) : Observable<boolean>
  {
    return this.messaging.successful(
      new NewPasswordClientResponse(
        this.hasher.hexOf(this.salter.salt(newPassword)))
      ).do(() => this.authSucceeded());
  }

  public guestLogin () : Observable<boolean>
  {
    return this.messaging.send(new StartGuestSessionRequest()).map(
      (reply : ReplyMessage) => {
        if (AcknowledgmentResponse.isOk(reply))
        {
          this.state = AuthState.Authenticated;
          return true;
        }
        return false;
      });
  }
}

/**
 * An array of the various services utilized for authentication that the
 * application module must provide to its children.
 */
export const AUTH_PROVIDERS : any[] = [
  AuthService,
  Hasher,
  Salter
];



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/auth.service.ts